import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { finalize, Subject, take } from 'rxjs';
import { MetaService } from '../../services/meta.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";

@Component({
  selector: 'app-meta',
  standalone: true,
  templateUrl: './meta.component.html',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    SharedTableComponent,
    SearchBarComponent,
    LoadingComponent
],
})
export class MetaComponent implements OnInit, OnDestroy {

  isLoading = false;
  hasError = false;
  showFullScreenLoading = false; //loading full screen

  metaList$ = new Subject<void>();

  // Stato query
  sortBy = 'name:asc';
  sortField = '';
  sortDirection = '';
  typeQuery: string = '';
  searchQuery: string = '';

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // Opzioni select "type"
  fieldTypesOptions = [{ value: '', label: '' }];

  // DataSource tabella
  metaDataSource = new MatTableDataSource<any>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly metaService: MetaService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // primo load senza filtri
    this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, {});
    // Recupero tipi campo per la select
    this.metaService.getAllfieldTypes().subscribe(response => {
      const metaTypesOptions = response as Array<{ value: string; label: string }>;
      this.fieldTypesOptions = metaTypesOptions.map((x: any) => ({
        value: x.label,
        label: x.label
      }));
    });
  }

  private currentFilter() {
    const q = this.typeQuery?.trim() ?? '';
    const s = this.searchQuery?.trim() ?? '';
    return q ? { search: s, type: q } : { search: s };
  }

  loadMetaData(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    fieldFilter: any
  ): void {
    this.isLoading = true;
    this.hasError = false;
    this.metaService.getMetaList(pageIndex, pageSize, sortBy, fieldFilter).pipe(finalize(() => { 
      this.isLoading = false;
      this.metaList$.next();
     })).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.metaDataSource.data = content;
        this.totalItems = response?.totalElements ?? 0;
        this.pageIndex = (response?.number ?? pageIndex) ?? 0;
        this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.metaDataSource.data = [];
        this.totalItems = 0;
        this.hasError = true;
      },
    });
  }

  // Filtro
  onFilterChanged(value: any) {
    this.typeQuery = value?.type ?? '';
    this.searchQuery = value?.name ?? '';
    this.pageIndex = 0;
    this.loadMetaData(this.pageIndex, this.pageSize, 'name:asc', this.currentFilter());
  }

  // Ordinamento
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
  }

  // Cambio pagina / page size (server-side)
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize  = ev.pageSize;
    this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: event.data,
        visibleFields: ['name', 'type'],
        hiddenFields: ['creationDate', 'system'],
        hiddenFieldsValues: {
          creationDate: new Date().toISOString(),
          system: false
        },
        fieldTypes: {
          name: 'input',
          type: 'select'
        },
        selectOptions: {
          type: this.fieldTypesOptions
        },
        labels: {
          name: 'Field',
          type: 'Tipologia'
        }
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.showFullScreenLoading = true;

      if (event.type === 'add') {
        this.metaService.addNewField(result).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            
            this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
            this.metaList$.pipe(take(1)).subscribe( () => {
              this.notificationService.entitySuccess('add', 'Field');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Field');

          }
        });

      } else if (event.type === 'edit') {
        const index = this.metaDataSource.data.findIndex((e: any) => e.id === result.id);
        if (index !== -1) {
          this.metaService.editField(result).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
            next: () => {
              
              this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
              this.metaList$.pipe(take(1)).subscribe( () => {
                this.notificationService.entitySuccess('update', 'Field');
              })
            },
            error: (err) => {
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('update', 'Field');
            }
          });
        }

      } else if (event.type === 'delete') {
        this.metaService.deleteField(result.id).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            if (this.pageIndex > 0 && this.metaDataSource.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            
            this.loadMetaData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
            this.metaList$.pipe(take(1)).subscribe( () => {
              this.notificationService.entitySuccess('delete', 'Field');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('delete', 'Field');
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.metaList$.complete();
  }
}